import { APIClient } from "@heidelbergmaterials/fcl-apiclient"
import { AuthenticationFlow } from "@heidelbergmaterials/fcl-authclient"
import { useEffect, useMemo } from "react"

import {
	correlationIdInterceptor,
	statusCheckInterceptor,
} from "./interceptors"

const configuredClients = new WeakSet<APIClient>()

interface UseApiClientOptions {
	baseURL: string
	authFlow?: AuthenticationFlow | null
	useInterceptors?: boolean
}

/**
 * Base hook to create an APIClient with optional custom interceptors
 *
 * This hook provides a reusable pattern for creating APIClient instances
 * with optional authentication and custom interceptors (correlation ID and status check).
 *
 * @param options Configuration options for the API client
 * @param options.baseURL The base URL for the API
 * @param options.authFlow Optional authentication flow for token management
 * @param options.useInterceptors Whether to add custom interceptors (default: true)
 * @returns APIClient instance configured with specified options
 *
 * @example
 * ```tsx
 * // With authentication and interceptors
 * const apiClient = useBaseApiClient({
 *   baseURL: 'https://api.example.com',
 *   authFlow: myAuthFlow,
 *   useInterceptors: true
 * })
 *
 * // Without authentication, no interceptors
 * const legalClient = useBaseApiClient({
 *   baseURL: 'https://legal-api.example.com',
 *   authFlow: null,
 *   useInterceptors: false
 * })
 * ```
 */
export const useBaseApiClient = ({
	baseURL,
	authFlow = null,
	useInterceptors = true,
}: UseApiClientOptions): APIClient => {
	const defaultHeaders = {
		"Content-Type": "application/json",
	}
	const apiClient = useMemo(
		() => new APIClient(baseURL, authFlow, defaultHeaders),
		[baseURL, authFlow],
	)

	useEffect(() => {
		if (!useInterceptors) return

		if (configuredClients.has(apiClient)) {
			return
		}

		// Access internal axios instance to add custom interceptors
		const axiosInstance = (apiClient as any).axiosInstance

		if (!axiosInstance) {
			console.error("Unable to access axios instance from APIClient")
			return
		}

		// Add correlation ID interceptor for analytics
		axiosInstance.interceptors.request.use(correlationIdInterceptor)

		// Add status check interceptor for error routing
		axiosInstance.interceptors.response.use(
			(response: any) => response,
			statusCheckInterceptor,
		)

		configuredClients.add(apiClient)
	}, [apiClient, useInterceptors])

	return apiClient
}
